import { useState } from 'react';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  DD,
  DescriptionList,
  DT,
  ErrorText,
  Field,
  FormGrid,
  Input,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Select,
  Stack,
} from '../../components/ui';
import { MY_PROVIDER_TAX_INFO, MY_SERVICE_PROVIDER } from '../../services/serviceService/queries';
import {
  useMyProviderTaxInfoQuery,
  useSubmitProviderTaxInfoMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { CheckRow } from './serviceControls';
import { serviceErrorMessage } from './serviceErrors';
import { PROVIDER_WORKERS_PATH } from './servicePaths';
import {
  classificationLabel,
  EMPTY_TAX_INFO,
  maskedTin,
  TAX_CLASSIFICATIONS,
  taxInfoProblems,
  toTaxInfoInput,
  type TaxInfoErrors,
  type TaxInfoForm,
  type TinType,
} from './taxInfo';

/**
 * The electronic W-9 (spec 019 W8a, D28). Needed before a worker can be
 * listed and before any payout. The full SSN or EIN is sent once and stored
 * encrypted; this page only ever shows its last 4 digits again.
 */
export default function ProviderTaxInfoPage() {
  const query = useMyProviderTaxInfoQuery({ fetchPolicy: 'network-only' });
  const saved = query.data?.myProviderTaxInfo ?? null;
  const [submit, { loading }] = useSubmitProviderTaxInfoMutation({
    refetchQueries: [{ query: MY_PROVIDER_TAX_INFO }, { query: MY_SERVICE_PROVIDER }],
  });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<TaxInfoForm>(EMPTY_TAX_INFO);
  const [errors, setErrors] = useState<TaxInfoErrors>({});
  const [message, setMessage] = useState<string | null>(null);

  const set = (patch: Partial<TaxInfoForm>) => {
    setForm(previous => ({ ...previous, ...patch }));
    setErrors(previous => {
      const next = { ...previous };
      Object.keys(patch).forEach(key => delete next[key as keyof TaxInfoForm]);
      return next;
    });
  };

  const onSubmit = async () => {
    const found = taxInfoProblems(form);
    setErrors(found);
    if (Object.keys(found).length) return;
    setMessage(null);
    try {
      await submit({ variables: { input: toTaxInfoInput(form) } });
      toast.success('Tax information saved');
      setForm(EMPTY_TAX_INFO);
      setEditing(false);
    } catch (e) {
      setMessage(serviceErrorMessage(e, 'Could not save your tax information'));
    }
  };

  const showForm = editing || (!query.loading && !saved);

  return (
    <PageContainer width="md" canvas data-testid="provider-tax-info">
      <PageHeader
        eyebrow="Provider"
        title="Tax information"
        description="We need a W-9 to pay you and to send you a 1099 form when the law requires it."
        backTo={{ to: PROVIDER_WORKERS_PATH, label: 'Workers' }}
      />
      {query.loading && !query.data ? (
        <LoadingBlock label="Loading" />
      ) : (
        <Stack $gap={6}>
          {saved && !editing && (
            <Card data-testid="tax-info-summary">
              <CardHeader
                title="On file"
                actions={
                  <Button
                    type="button"
                    theme="secondary"
                    size="sm"
                    text="Replace"
                    onClick={() => setEditing(true)}
                  />
                }
              />
              <CardBody>
                <DescriptionList>
                  <DT>Name</DT>
                  <DD>{saved.legalName}</DD>
                  <DT>Classification</DT>
                  <DD>{classificationLabel(saved.classification)}</DD>
                  <DT>Tax number</DT>
                  <DD data-testid="tax-info-tin">{maskedTin(saved.tinType, saved.tinLast4)}</DD>
                  <DT>Signed</DT>
                  <DD>{DateTime.fromISO(saved.signedAt).toLocaleString(DateTime.DATE_MED)}</DD>
                </DescriptionList>
              </CardBody>
            </Card>
          )}
          {showForm && (
            <Card>
              <CardHeader
                title="Form W-9"
                description="Request for taxpayer identification number and certification."
              />
              <CardBody>
                <Stack $gap={4}>
                  {message && (
                    <Alert tone="error" title="Not saved">
                      {message}
                    </Alert>
                  )}
                  <Field
                    label="Name on your tax return"
                    htmlFor="tax-name"
                    required
                    error={errors.legalName}
                    hint="Your own name, or the business name for an LLC or company."
                  >
                    <Input
                      id="tax-name"
                      value={form.legalName}
                      onChange={e => set({ legalName: e.target.value })}
                      data-testid="tax-name"
                    />
                  </Field>
                  <Field
                    label="Tax classification"
                    htmlFor="tax-class"
                    required
                    error={errors.classification}
                  >
                    <Select
                      id="tax-class"
                      value={form.classification}
                      onChange={e => set({ classification: e.target.value })}
                    >
                      {TAX_CLASSIFICATIONS.map(c => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Street address" htmlFor="tax-line1" required error={errors.line1}>
                    <Input
                      id="tax-line1"
                      value={form.line1}
                      onChange={e => set({ line1: e.target.value })}
                      data-testid="tax-line1"
                    />
                  </Field>
                  <Field label="Apartment or suite" htmlFor="tax-line2" hint="Optional">
                    <Input
                      id="tax-line2"
                      value={form.line2}
                      onChange={e => set({ line2: e.target.value })}
                    />
                  </Field>
                  <FormGrid $cols={3}>
                    <Field label="City" htmlFor="tax-city" required error={errors.city}>
                      <Input
                        id="tax-city"
                        value={form.city}
                        onChange={e => set({ city: e.target.value })}
                        data-testid="tax-city"
                      />
                    </Field>
                    <Field label="State" htmlFor="tax-state" required error={errors.state}>
                      <Input
                        id="tax-state"
                        value={form.state}
                        maxLength={2}
                        placeholder="AZ"
                        onChange={e => set({ state: e.target.value })}
                        data-testid="tax-state"
                      />
                    </Field>
                    <Field label="ZIP code" htmlFor="tax-zip" required error={errors.zip}>
                      <Input
                        id="tax-zip"
                        value={form.zip}
                        inputMode="numeric"
                        onChange={e => set({ zip: e.target.value })}
                        data-testid="tax-zip"
                      />
                    </Field>
                  </FormGrid>
                  <FormGrid>
                    <Field label="Number type" htmlFor="tax-tin-type" required>
                      <Select
                        id="tax-tin-type"
                        value={form.tinType}
                        onChange={e => set({ tinType: e.target.value as TinType, tin: '' })}
                      >
                        <option value="ssn">Social Security number (SSN)</option>
                        <option value="ein">Employer ID number (EIN)</option>
                      </Select>
                    </Field>
                    <Field
                      label={form.tinType === 'ssn' ? 'SSN' : 'EIN'}
                      htmlFor="tax-tin"
                      required
                      error={errors.tin}
                      hint="Stored encrypted. We only show the last 4 digits."
                    >
                      <Input
                        id="tax-tin"
                        type="password"
                        autoComplete="off"
                        inputMode="numeric"
                        value={form.tin}
                        placeholder={form.tinType === 'ssn' ? '123-45-6789' : '12-3456789'}
                        onChange={e => set({ tin: e.target.value })}
                        data-testid="tax-tin"
                      />
                    </Field>
                  </FormGrid>
                  <Muted $size="sm">
                    Under penalties of perjury, I certify that the number on this form is my correct
                    taxpayer identification number, that I am not subject to backup withholding, and
                    that I am a U.S. person.
                  </Muted>
                  <CheckRow
                    label="I certify the statements above"
                    checked={form.certified}
                    onChange={certified => set({ certified })}
                    testId="tax-certify"
                  />
                  {errors.certified && <ErrorText role="alert">{errors.certified}</ErrorText>}
                  <Field
                    label="Type your name to sign"
                    htmlFor="tax-sign"
                    required
                    error={errors.signedName}
                    hint="The same name as above."
                  >
                    <Input
                      id="tax-sign"
                      value={form.signedName}
                      onChange={e => set({ signedName: e.target.value })}
                      data-testid="tax-sign"
                    />
                  </Field>
                  <InlineRow>
                    <Button
                      type="button"
                      text="Sign and save"
                      onClick={onSubmit}
                      isLoading={loading}
                      dataTestId="tax-submit"
                    />
                    {saved && (
                      <Button
                        type="button"
                        theme="text"
                        text="Cancel"
                        onClick={() => setEditing(false)}
                      />
                    )}
                  </InlineRow>
                </Stack>
              </CardBody>
            </Card>
          )}
        </Stack>
      )}
    </PageContainer>
  );
}
